import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { FALLBACK_TESTING, money } from "../data/site";
import { CATEGORIES } from "../data/biomarkers";
import { getCategories, getTestingOption, getTestingOptions } from "../services/veysoApi";
import { useApiData } from "../hooks/useApiData";
import FallbackNotice from "../components/FallbackNotice";
import { Card, Cards, Eyebrow, Glass, HeroPanel, PageHero, Section, heroButtonSx } from "../components/ui";
import { tokens } from "../theme";

export function TestingList() {
  const { data, usingFallback } = useApiData(getTestingOptions, FALLBACK_TESTING);
  return (
    <>
      <HeroPanel theme="basalt" eyebrow="Testing" title="Health starts with clarity"
        subtitle="A range of testing options, available à la carte or with membership benefits."
        actions={<>
          <Button size="large" component={RouterLink} to="/testing/blood" sx={heroButtonSx("basalt")}>Start your panel</Button>
          <Button size="large" component={RouterLink} to="/testing/compare" sx={heroButtonSx("basalt", "ghost")}>Compare tests</Button>
        </>}
        minHeight={480}
      />
      <Section>
        <FallbackNotice show={usingFallback} />
        <Cards min={280}>
          {(data || []).map((t) => (
            <Card key={t.slug} sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h4" sx={{ fontSize: 22 }}>{t.name}</Typography>
              <Typography sx={{ color: tokens.muted, fontSize: 14.5, mt: 0.5 }}>{t.tagline}</Typography>
              <Typography sx={{ mt: 2, flex: 1, fontSize: 15 }}>{t.detail}</Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
                <Typography sx={{ color: tokens.muted, fontSize: 14 }}>from {money(t.fromCents)}</Typography>
                <Button component={RouterLink} to={`/testing/${t.slug}`} variant="outlined" size="small">Learn more</Button>
              </Box>
            </Card>
          ))}
        </Cards>
      </Section>
    </>
  );
}

export function TestingDetail() {
  const { slug } = useParams();
  const fallback = FALLBACK_TESTING.find((t) => t.slug === slug) || null;
  const { data: t, loading, usingFallback } = useApiData(() => getTestingOption(slug), fallback, { deps: [slug] });
  const { data: cats } = useApiData(getCategories, CATEGORIES);
  if (loading) return null;
  if (!t) {
    return <PageHero theme="basalt" eyebrow="Testing" title="Test not found" actions={<Button component={RouterLink} to="/testing" sx={heroButtonSx("basalt")}>All testing options</Button>} />;
  }
  return (
    <>
      <HeroPanel theme="basalt" eyebrow={t.tagline} title={t.name} subtitle={t.detail} minHeight={460}
        actions={<Button size="large" component={RouterLink} to="/cart" sx={heroButtonSx("basalt")}>Order from {money(t.fromCents)}</Button>}
        visual={slug === "blood" ? (
          <Glass light sx={{ maxWidth: 420, ml: { md: "auto" } }}>
            <Typography sx={{ fontSize: 12.5, letterSpacing: ".1em", color: tokens.muted, mb: 1 }}>WHAT WE MEASURE (SAMPLE)</Typography>
            {(cats || []).slice(0, 5).map((c) => (
              <Box key={c.id} sx={{ display: "flex", justifyContent: "space-between", py: 0.8, borderBottom: `1px solid ${tokens.line}` }}>
                <Typography sx={{ fontSize: 14.5 }}>{c.name}</Typography><Typography sx={{ fontSize: 14, color: tokens.muted }}>{c.count}</Typography>
              </Box>
            ))}
          </Glass>
        ) : null}
      />
      <Section tone="white" narrow>
        <FallbackNotice show={usingFallback} />
        <Eyebrow>How it works</Eyebrow>
        <Typography sx={{ color: tokens.muted }}>Order the test, collect your sample, and see results in your VEYSO profile with plain-language explanations. If care may help, you can start an online visit with a licensed provider. Sample page.</Typography>
        <Button sx={{ mt: 3 }} component={RouterLink} to="/testing">All testing options</Button>
      </Section>
    </>
  );
}

export function TestingCompare() {
  const { data, usingFallback } = useApiData(getTestingOptions, FALLBACK_TESTING);
  return (
    <>
      <PageHero theme="basalt" eyebrow="Testing" title="Compare tests" subtitle="Side by side, from the same sample catalog." />
      <Section tone="white">
        <FallbackNotice show={usingFallback} />
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 640, "& th, & td": { textAlign: "left", p: 1.75, borderBottom: `1px solid ${tokens.line}`, fontSize: 15 } }}>
            <thead><tr><th>Test</th><th>Focus</th><th>From</th><th /></tr></thead>
            <tbody>
              {(data || []).map((t) => (
                <tr key={t.slug}><td style={{ fontWeight: 500 }}>{t.name}</td><td>{t.tagline}</td><td>{money(t.fromCents)}</td>
                  <td><Button size="small" component={RouterLink} to={`/testing/${t.slug}`}>Details</Button></td></tr>
              ))}
            </tbody>
          </Box>
        </Box>
      </Section>
    </>
  );
}
